/**
 * Track Guitar Effects Panel
 * Per-track guitar amp and pedal controls for DAW integration
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { Switch } from '../../ui/switch';
import {
  Guitar,
  Power,
  ChevronDown,
  ChevronUp,
  Zap,
  Settings,
  RotateCcw,
  Plus,
  X,
  Volume2,
  Flame,
  Waves,
  Music,
  Speaker,
  Lock,
  Crown,
  Star,
} from 'lucide-react';
import { GuitarPresetSelector } from './GuitarPresetSelector';
import { useSubscription } from '../../../context/SubscriptionContext';
import { EffectsFavoritesPanel, FavoriteToggleButton, useEffectFavorites } from './EffectsFavoritesPanel';

// Compact amp model selector - All 13 amp models
const AMP_MODELS = [
  { id: 'clean', name: 'Clean Twin', icon: Music, color: 'cyan', tier: 'free' },
  { id: 'crunch', name: 'British Crunch', icon: Zap, color: 'amber', tier: 'free' },
  { id: 'highGain', name: 'High Gain', icon: Flame, color: 'red', tier: 'pro' },
  { id: 'metal', name: 'Metal Machine', icon: Guitar, color: 'violet', tier: 'pro' },
  { id: 'acoustic', name: 'Acoustic Sim', icon: Music, color: 'emerald', tier: 'free' },
  { id: 'britishStack', name: 'British Stack', icon: Zap, color: 'yellow', tier: 'pro' },
  { id: 'vintageDeluxe', name: 'Vintage Deluxe', icon: Music, color: 'sky', tier: 'free' },
  { id: 'modernMetal', name: 'Modern Metal', icon: Flame, color: 'zinc', tier: 'studio' },
  { id: 'bassDriver', name: 'Bass Driver', icon: Volume2, color: 'indigo', tier: 'pro' },
  { id: 'jazzClean', name: 'Jazz Clean', icon: Music, color: 'amber', tier: 'free' },
  { id: 'bluesBreaker', name: 'Blues Breaker', icon: Waves, color: 'blue', tier: 'pro' },
  { id: 'matchless', name: 'Boutique AC', icon: Music, color: 'rose', tier: 'studio' },
  { id: 'dualRecto', name: 'Dual Rectifier', icon: Flame, color: 'orange', tier: 'studio' },
];

// Available pedal types - All 20 pedal effects
const PEDAL_TYPES = [
  // Drive pedals
  { id: 'distortion', name: 'Distortion', color: 'orange', category: 'drive' },
  { id: 'overdrive', name: 'Overdrive', color: 'amber', category: 'drive' },
  { id: 'fuzz', name: 'Fuzz', color: 'violet', category: 'drive' },
  { id: 'booster', name: 'Clean Boost', color: 'yellow', category: 'drive' },
  // Filter pedals
  { id: 'wah', name: 'Wah', color: 'emerald', category: 'filter' },
  { id: 'autoWah', name: 'Auto Wah', color: 'lime', category: 'filter' },
  // Modulation pedals
  { id: 'chorus', name: 'Chorus', color: 'cyan', category: 'modulation' },
  { id: 'phaser', name: 'Phaser', color: 'pink', category: 'modulation' },
  { id: 'flanger', name: 'Flanger', color: 'indigo', category: 'modulation' },
  { id: 'tremolo', name: 'Tremolo', color: 'rose', category: 'modulation' },
  { id: 'vibrato', name: 'Vibrato', color: 'orange', category: 'modulation' },
  { id: 'rotary', name: 'Rotary', color: 'amber', category: 'modulation' },
  // Pitch pedals
  { id: 'octaver', name: 'Octaver', color: 'purple', category: 'pitch' },
  { id: 'pitchShifter', name: 'Pitch Shifter', color: 'teal', category: 'pitch' },
  // Time pedals
  { id: 'delay', name: 'Delay', color: 'blue', category: 'time' },
  { id: 'reverb', name: 'Reverb', color: 'sky', category: 'time' },
  // Dynamics pedals
  { id: 'compressor', name: 'Compressor', color: 'slate', category: 'dynamics' },
  { id: 'noisegate', name: 'Noise Gate', color: 'zinc', category: 'dynamics' },
  // Utility pedals
  { id: 'looper', name: 'Looper', color: 'green', category: 'utility' },
  { id: 'eqPedal', name: 'EQ Pedal', color: 'gray', category: 'utility' },
];

// Compact knob for amp controls
const CompactKnob = ({ value, onChange, label, color = 'violet' }) => {
  const rotation = ((value / 100) * 270) - 135;
  
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div 
        className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-600 relative cursor-pointer hover:border-zinc-500 transition-colors"
        onMouseDown={(e) => {
          const startY = e.clientY;
          const startValue = value;
          
          const handleMove = (moveEvent) => {
            const delta = (startY - moveEvent.clientY) * 0.8;
            const newValue = Math.max(0, Math.min(100, startValue + delta));
            onChange(Math.round(newValue));
          };
          
          const handleUp = () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
          };
          
          document.addEventListener('mousemove', handleMove);
          document.addEventListener('mouseup', handleUp);
        }}
      >
        <div 
          className={`absolute top-1/2 left-1/2 w-0.5 h-3 bg-${color}-400 rounded-full origin-bottom`}
          style={{ transform: `translate(-50%, -100%) rotate(${rotation}deg)` }}
        />
      </div>
      <span className="text-[9px] text-zinc-500 uppercase">{label}</span>
    </div>
  );
};

// Mini pedal display
const MiniPedal = ({ pedal, onToggle, onRemove }) => {
  const pedalType = PEDAL_TYPES.find(p => p.id === pedal.type);
  
  return (
    <div 
      className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md border transition-all ${
        pedal.enabled 
          ? `bg-${pedalType?.color || 'violet'}-500/20 border-${pedalType?.color || 'violet'}-500/40`
          : 'bg-zinc-800/50 border-zinc-700'
      }`}
    >
      <button
        onClick={() => onToggle(pedal.id)}
        className={`w-2 h-2 rounded-full ${pedal.enabled ? 'bg-red-500' : 'bg-zinc-600'}`}
      />
      <span className={`text-[10px] ${pedal.enabled ? 'text-white' : 'text-zinc-500'}`}>
        {pedalType?.name || pedal.type}
      </span>
      <button 
        onClick={() => onRemove(pedal.id)}
        className="text-zinc-500 hover:text-white ml-1"
      >
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  );
};

export const TrackGuitarEffects = ({
  trackId,
  trackName,
  guitarEffects = null,
  onGuitarEffectsChange,
  isExpanded = false,
  onToggleExpand,
  userId = null,
  currentPresetId = null,
  onPresetSelect,
}) => {
  const [showPedalPicker, setShowPedalPicker] = useState(false);
  const [showFavoritesPanel, setShowFavoritesPanel] = useState(true);
  const navigate = useNavigate();
  const { isPro, isStudio, getFeatureLimit } = useSubscription();
  const { recordEffectUsage } = useEffectFavorites();
  
  // Get allowed amp models from subscription
  const allowedAmpModels = getFeatureLimit('amp_models') || ['clean', 'crunch'];
  
  // Check if an amp model is available for the user's tier
  const isAmpModelAvailable = useCallback((modelId) => {
    return allowedAmpModels.includes(modelId);
  }, [allowedAmpModels]);
  
  // Default guitar effects state
  const defaultEffects = {
    enabled: false,
    ampEnabled: false,
    ampModel: 'crunch',
    ampControls: { gain: 50, bass: 50, mid: 50, treble: 50, presence: 50, master: 60 },
    cabinet: '4x12',
    pedals: [],
    presetId: null,
  };
  
  const effects = guitarEffects || defaultEffects;
  
  // Handle preset selection
  const handlePresetSelect = useCallback((trackId, settings, presetId) => {
    onGuitarEffectsChange?.(trackId, {
      ...settings,
      presetId,
    });
    onPresetSelect?.(trackId, presetId);
  }, [onGuitarEffectsChange, onPresetSelect]);
  
  // Handle enabling/disabling guitar effects
  const handleToggleEnabled = useCallback(() => {
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      enabled: !effects.enabled,
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle amp enable/disable
  const handleToggleAmp = useCallback(() => {
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      ampEnabled: !effects.ampEnabled,
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle amp model change
  const handleAmpModelChange = useCallback((model) => {
    // Check if model is available for user's tier
    if (!isAmpModelAvailable(model)) {
      // Show upgrade prompt
      return;
    }
    
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      ampModel: model,
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle amp control change
  const handleAmpControlChange = useCallback((param, value) => {
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      ampControls: { ...effects.ampControls, [param]: value },
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle adding pedal
  const handleAddPedal = useCallback((pedalType) => {
    const newPedal = {
      id: `pedal_${Date.now()}`,
      type: pedalType,
      enabled: true,
      params: {},
    };
    
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      pedals: [...effects.pedals, newPedal],
    });
    setShowPedalPicker(false);
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle toggling pedal
  const handleTogglePedal = useCallback((pedalId) => {
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      pedals: effects.pedals.map(p => 
        p.id === pedalId ? { ...p, enabled: !p.enabled } : p
      ),
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle removing pedal
  const handleRemovePedal = useCallback((pedalId) => {
    onGuitarEffectsChange?.(trackId, {
      ...effects,
      pedals: effects.pedals.filter(p => p.id !== pedalId),
    });
  }, [trackId, effects, onGuitarEffectsChange]);
  
  // Handle reset
  const handleReset = useCallback(() => {
    onGuitarEffectsChange?.(trackId, defaultEffects);
  }, [trackId, onGuitarEffectsChange]);
  
  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${
      effects.enabled ? 'border-orange-500/30 bg-orange-500/5' : 'border-zinc-700/50 bg-zinc-800/30'
    }`}>
      {/* Header - Always visible */}
      <div 
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-zinc-800/50"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-2">
          <Guitar className={`w-4 h-4 ${effects.enabled ? 'text-orange-400' : 'text-zinc-500'}`} />
          <span className={`text-xs font-medium ${effects.enabled ? 'text-white' : 'text-zinc-400'}`}>
            Guitar Effects
          </span>
          {effects.enabled && effects.ampEnabled && (
            <span className="text-[10px] text-orange-400 bg-orange-500/20 px-1.5 py-0.5 rounded">
              {AMP_MODELS.find(a => a.id === effects.ampModel)?.name}
            </span>
          )}
          {effects.enabled && effects.pedals.filter(p => p.enabled).length > 0 && (
            <span className="text-[10px] text-violet-400 bg-violet-500/20 px-1.5 py-0.5 rounded">
              {effects.pedals.filter(p => p.enabled).length} pedals
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Switch
            checked={effects.enabled}
            onCheckedChange={handleToggleEnabled}
            onClick={(e) => e.stopPropagation()}
            className="scale-75"
          />
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-zinc-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-zinc-500" />
          )}
        </div>
      </div>
      
      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && effects.enabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3 border-t border-zinc-700/50">
              {/* Quick Effects / Favorites Panel */}
              <div className="pt-3">
                <EffectsFavoritesPanel
                  ampModels={AMP_MODELS}
                  pedalTypes={PEDAL_TYPES}
                  onSelectAmp={(ampId) => {
                    if (isAmpModelAvailable(ampId)) {
                      handleAmpModelChange(ampId);
                      recordEffectUsage('amp', ampId);
                    } else {
                      navigate('/pricing');
                    }
                  }}
                  onSelectPedal={(pedalId) => {
                    handleAddPedal(pedalId);
                    recordEffectUsage('pedal', pedalId);
                  }}
                  isExpanded={showFavoritesPanel}
                  onToggleExpand={() => setShowFavoritesPanel(!showFavoritesPanel)}
                  currentAmpModel={effects.ampModel}
                  activePedals={effects.pedals}
                />
              </div>
              
              {/* Preset Selector */}
              <div className="pt-2">
                <GuitarPresetSelector
                  trackId={trackId}
                  currentPresetId={effects.presetId || currentPresetId}
                  currentSettings={effects}
                  onSelectPreset={handlePresetSelect}
                  userId={userId}
                  compact={false}
                  showSaveButton={effects.ampEnabled}
                />
              </div>
              
              {/* Amp Section */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Speaker className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs text-zinc-300">Amp Simulator</span>
                  </div>
                  <Switch
                    checked={effects.ampEnabled}
                    onCheckedChange={handleToggleAmp}
                    className="scale-75"
                  />
                </div>
                
                {effects.ampEnabled && (
                  <div className="space-y-3">
                    {/* Amp model buttons - scrollable horizontal list */}
                    <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                      {AMP_MODELS.map(model => {
                        const Icon = model.icon;
                        const isLocked = !isAmpModelAvailable(model.id);
                        const isSelected = effects.ampModel === model.id;
                        
                        return (
                          <button
                            key={model.id}
                            onClick={() => {
                              if (isLocked) {
                                navigate('/pricing');
                              } else {
                                handleAmpModelChange(model.id);
                              }
                            }}
                            className={`group flex-shrink-0 flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] transition-all relative ${
                              isLocked
                                ? 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-600 cursor-pointer hover:border-amber-500/30'
                                : isSelected
                                  ? `bg-${model.color}-500/20 border border-${model.color}-500/50 text-${model.color}-400`
                                  : 'bg-zinc-800 border border-zinc-700 text-zinc-500 hover:text-white hover:border-zinc-600'
                            }`}
                            title={isLocked ? `Upgrade to unlock ${model.name}` : model.name}
                            data-testid={`amp-model-${model.id}`}
                          >
                            {isLocked ? (
                              <>
                                <Lock className="w-3 h-3 text-amber-500/70" />
                                <span className="opacity-60 whitespace-nowrap">{model.name}</span>
                                <Crown className="w-2.5 h-2.5 text-amber-500/70 absolute -top-1 -right-1" />
                              </>
                            ) : (
                              <>
                                <Icon className="w-3 h-3" />
                                <span className="whitespace-nowrap">{model.name}</span>
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5">
                                  <FavoriteToggleButton effectId={model.id} effectType="amp" size="xs" inline={true} />
                                </span>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Amp controls */}
                    <div className="flex justify-between px-1">
                      <CompactKnob 
                        value={effects.ampControls.gain} 
                        onChange={(v) => handleAmpControlChange('gain', v)} 
                        label="Gain"
                        color="orange"
                      />
                      <CompactKnob 
                        value={effects.ampControls.bass} 
                        onChange={(v) => handleAmpControlChange('bass', v)} 
                        label="Bass"
                        color="blue"
                      />
                      <CompactKnob 
                        value={effects.ampControls.mid} 
                        onChange={(v) => handleAmpControlChange('mid', v)} 
                        label="Mid"
                        color="emerald"
                      />
                      <CompactKnob 
                        value={effects.ampControls.treble} 
                        onChange={(v) => handleAmpControlChange('treble', v)} 
                        label="Treble"
                        color="yellow"
                      />
                      <CompactKnob 
                        value={effects.ampControls.master} 
                        onChange={(v) => handleAmpControlChange('master', v)} 
                        label="Master"
                        color="violet"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Pedal Board Section */}
              <div className="border-t border-zinc-700/50 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-violet-400" />
                    <span className="text-xs text-zinc-300">Pedal Board</span>
                    <span className="text-[10px] text-zinc-500">({effects.pedals.length})</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPedalPicker(true)}
                    className="h-6 px-2 text-[10px]"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                
                {/* Pedal chain display */}
                {effects.pedals.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {effects.pedals.map(pedal => (
                      <MiniPedal
                        key={pedal.id}
                        pedal={pedal}
                        onToggle={handleTogglePedal}
                        onRemove={handleRemovePedal}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-zinc-600 text-center py-2">
                    No pedals - click Add to build your chain
                  </p>
                )}
              </div>
              
              {/* Reset button */}
              <div className="flex justify-end pt-2 border-t border-zinc-700/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="h-6 px-2 text-[10px] text-zinc-500"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset
                </Button>
              </div>
            </div>
            
            {/* Pedal picker dropdown - uses fixed to escape overflow clipping */}
            <AnimatePresence>
              {showPedalPicker && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
                  onClick={() => setShowPedalPicker(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.9 }}
                    className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h4 className="text-sm font-medium text-white mb-3">Add Pedal</h4>
                    
                    {/* Pedals organized by category */}
                    {['drive', 'filter', 'modulation', 'pitch', 'time', 'dynamics', 'utility'].map(category => {
                      const categoryPedals = PEDAL_TYPES.filter(p => p.category === category);
                      if (categoryPedals.length === 0) return null;
                      
                      const categoryLabels = {
                        drive: { name: 'Drive', color: 'orange' },
                        filter: { name: 'Filter', color: 'emerald' },
                        modulation: { name: 'Modulation', color: 'cyan' },
                        pitch: { name: 'Pitch', color: 'purple' },
                        time: { name: 'Time', color: 'blue' },
                        dynamics: { name: 'Dynamics', color: 'slate' },
                        utility: { name: 'Utility', color: 'gray' },
                      };
                      
                      return (
                        <div key={category} className="mb-3">
                          <h5 className={`text-[10px] font-medium text-${categoryLabels[category].color}-400 mb-1.5 uppercase tracking-wider`}>
                            {categoryLabels[category].name}
                          </h5>
                          <div className="grid grid-cols-3 gap-1.5">
                            {categoryPedals.map(pedal => (
                              <button
                                key={pedal.id}
                                onClick={() => {
                                  handleAddPedal(pedal.id);
                                  recordEffectUsage('pedal', pedal.id);
                                }}
                                disabled={effects.pedals.some(p => p.type === pedal.id)}
                                data-testid={`pedal-picker-${pedal.id}`}
                                className={`group relative px-2 py-1.5 rounded-lg text-[10px] transition-all ${
                                  effects.pedals.some(p => p.type === pedal.id)
                                    ? 'bg-zinc-800/50 text-zinc-600 cursor-not-allowed'
                                    : `bg-${pedal.color}-500/20 border border-${pedal.color}-500/40 text-${pedal.color}-400 hover:bg-${pedal.color}-500/30`
                                }`}
                              >
                                <span className="flex items-center justify-center gap-1">
                                  {pedal.name}
                                  {!effects.pedals.some(p => p.type === pedal.id) && (
                                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                                      <FavoriteToggleButton effectId={pedal.id} effectType="pedal" size="xs" inline={true} />
                                    </span>
                                  )}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPedalPicker(false)}
                      className="w-full mt-3 text-xs"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrackGuitarEffects;
